import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

interface DeliveryFormProps {
  onSubmit: (deliveryInfo: DeliveryInfo) => void;
}

export interface DeliveryInfo {
  name: string;
  phone: string;
  area: string;
  address: string;
}

// Example areas — could be fetched from API later
const DELIVERY_AREAS = [
  "Manama",
  "Riffa",
  "Muharraq",
  "Isa Town",
  "Hamad Town",
  "Sanabis",
];

export const DeliveryForm: React.FC<DeliveryFormProps> = ({ onSubmit }) => {
  const [deliveryInfo, setDeliveryInfo] = useState<DeliveryInfo>({
    name: "",
    phone: "",
    area: "",
    address: "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof DeliveryInfo, string>>>({});

  const validate = () => {
    const newErrors: Partial<Record<keyof DeliveryInfo, string>> = {};

    if (!deliveryInfo.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!/^\d{8,}$/.test(deliveryInfo.phone.replace(/\D/g, ""))) {
      newErrors.phone = "Phone must be at least 8 digits";
    }

    if (!deliveryInfo.area.trim()) {
      newErrors.area = "Area is required";
    }

    if (!deliveryInfo.address.trim()) {
      newErrors.address = "Address is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSubmit(deliveryInfo);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDeliveryInfo((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: 400,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
        p: 3,
        height: "100%",
      }}
    >
      <Typography variant="h6" gutterBottom>
        Delivery Details
      </Typography>

      <TextField
        label="Customer Name"
        name="name"
        value={deliveryInfo.name}
        onChange={handleChange}
        onBlur={validate}
        error={!!errors.name}
        helperText={errors.name}
        fullWidth
        margin="normal"
      />

      <TextField
        label="Phone Number"
        name="phone"
        value={deliveryInfo.phone}
        onChange={handleChange}
        onBlur={validate}
        error={!!errors.phone}
        helperText={errors.phone}
        fullWidth
        margin="normal"
        placeholder="e.g. 39887766"
      />

      <Autocomplete
        options={DELIVERY_AREAS}
        freeSolo
        onInputChange={(e, newInputValue) => {
          setDeliveryInfo((prev) => ({ ...prev, area: newInputValue }));
        }}
        value={deliveryInfo.area}
        onChange={(e, newValue) =>
          setDeliveryInfo((prev) => ({ ...prev, area: newValue || "" }))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Delivery Area"
            name="area"
            onBlur={validate}
            error={!!errors.area}
            helperText={errors.area}
            fullWidth
            margin="normal"
          />
        )}
      />

      <TextField
        label="Full Address"
        name="address"
        value={deliveryInfo.address}
        onChange={handleChange}
        onBlur={validate}
        error={!!errors.address}
        helperText={errors.address}
        multiline
        rows={3}
        fullWidth
        margin="normal"
      />

      <Button
        fullWidth
        variant="contained"
        onClick={handleSubmit}
        sx={{ mt: 2, backgroundColor: "#1E8078" }}
      >
        Proceed to Order
      </Button>
    </Paper>
  );
};